import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import useEventSubscriber from '@/lib/hooks/useEventSubscriber'
import useLightboxRotationStore from '@/store/useLightboxRotationStore'
import { RotationReconciler } from '@/lib/mercure/rotationReconciliation'

/**
 * Subscribes to the per-rotate Mercure echo topic and clears the
 * matching optimistic state in `useLightboxRotationStore` once the
 * server confirms the rotate landed.
 *
 * **Clearance condition:**
 *
 *   1. The matching `rotationRequestUUID` event arrives on
 *      `/bg_sync/media_objects/rotate/{rotationRequestUUID}` —
 *      published by {@see App\EventListener\MediaObjectListener} after
 *      `setRotationRequestUUID()` is set on the entity during rotate.
 *
 *   2. The exact authoritative parent query has refreshed with the new
 *      cache-busted URLs. This subscriber performs that targeted refresh
 *      itself so it does not depend on sibling subscriber timing.
 *
 * A single `requestAnimationFrame` was NOT enough for step 2: the
 * invalidation kicks off a network refetch that takes far longer than
 * one frame, so the optimistic entry was dropped while the cache still
 * held the PRE-rotation annotation. Both the lightbox overlay and the
 * grid thumbnail then briefly rendered the canonical (un-rotated)
 * strokes over the already-rotated bitmap — the "markup ~90° out of sync
 * vs the thumbnail, re-syncs on the next rotate" report. We now defer
 * the clear until the targeted parent refetch settles, with a bounded
 * wait so a stuck request can never strand the optimistic state.
 *
 * Dimensions are NOT a proof of landing — a 180° rotate or a square
 * photo preserves W/H, so a dimensions-only check would silently miss
 * those cases (BAY-35 plan, "Mercure clear-condition" rule). Hence we
 * gate on refetch-settle, not on a dimension comparison. If the echo is
 * lost, a bounded watchdog performs the same targeted refresh. A clean
 * HTTP acknowledgement is sufficient proof; an opaque native response
 * must additionally observe a changed server URL before clearing.
 *
 * Mounted by `RootLayout` so the subscription survives lightbox
 * open/close cycles. Mercure URI-template wildcard
 * `{rotationRequestUUID}` matches every rotate echo with a single
 * subscription — no per-entry EventSource churn.
 */
export default function useMediaObjectRotationSubscriber(): void {
  const entries = useLightboxRotationStore(s => s.entries)
  const queryClient = useQueryClient()
  const reconcilerRef = useRef<RotationReconciler | null>(null)

  useEffect(() => {
    const reconciler = new RotationReconciler({
      clearForRequest: rotationRequestUUID =>
        useLightboxRotationStore.getState().clearForRequest(rotationRequestUUID),
      getEntryByRequest: rotationRequestUUID => {
        const match = Object.entries(useLightboxRotationStore.getState().entries).find(
          ([, entry]) => entry.rotationRequestUUID === rotationRequestUUID,
        )
        return match ? { mediaObjectId: match[0], entry: match[1] } : undefined
      },
      queryClient,
    })
    reconcilerRef.current = reconciler
    reconciler.sync(useLightboxRotationStore.getState().entries)

    return () => {
      reconciler.dispose()
      if (reconcilerRef.current === reconciler) reconcilerRef.current = null
    }
  }, [queryClient])

  useEffect(() => {
    reconcilerRef.current?.sync(entries)
  }, [entries])

  const topic = '/bg_sync/media_objects/rotate/{rotationRequestUUID}'

  useEventSubscriber(topic, event => {
    try {
      const payload = JSON.parse(event.data) as { rotationRequestUUID?: string } | undefined
      const uuid = payload?.rotationRequestUUID
      if (typeof uuid !== 'string' || uuid === '') return
      void reconcilerRef.current?.handleEcho(uuid)
    } catch {
      // Malformed payload is ignored; the watchdog remains available.
    }
  })
}
